import { useState } from "react";
import { AlertTriangle, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";

const LEVEL_STYLE = {
  green: { color: "#00C853", label: "All Systems Normal", Icon: CheckCircle2 },
  amber: { color: "#FFB300", label: "Attention Needed", Icon: AlertCircle },
  red: { color: "#FF3B30", label: "Immediate Attention", Icon: AlertTriangle },
};

export function PlantStatusBadge() {
  const { data } = usePolling(() => api.get("/plant-status").then((r) => r.data), 6000);
  const [open, setOpen] = useState(false);
  const level = data?.level || "green";
  const style = LEVEL_STYLE[level];
  const { Icon } = style;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors"
        style={{ borderColor: style.color, backgroundColor: `${style.color}1A` }}
        data-testid="plant-status-badge"
        data-plant-status-level={level}
      >
        <Icon className={`w-3.5 h-3.5 ${level === "red" ? "pulse-glow" : ""}`} style={{ color: style.color }} />
        <span className="text-[10px] font-heading uppercase tracking-wide hidden sm:inline" style={{ color: style.color }}>
          {style.label}
        </span>
        {data?.active_fault_count > 0 && (
          <span className="text-[9px] font-mono-data px-1 rounded" style={{ color: style.color }} data-testid="plant-status-fault-count">
            {data.active_fault_count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-2 w-72 rounded-lg border shadow-2xl p-3 z-50"
          style={{ borderColor: "var(--oim-border)", backgroundColor: "var(--oim-surface)" }}
          data-testid="plant-status-dropdown"
        >
          <div className="text-[10px] uppercase tracking-widest text-[var(--oim-text-dim)] font-heading mb-2">Plant Status Reasons</div>
          {(!data?.reasons || data.reasons.length === 0) ? (
            <div className="text-xs text-[var(--oim-text-muted)]" data-testid="plant-status-no-reasons">No active faults or safety-critical breaches — plant is in normal operating condition.</div>
          ) : (
            <div className="space-y-1.5">
              {data.reasons.map((r, i) => (
                <div key={i} className="flex items-start gap-2 text-xs" data-testid={`plant-status-reason-${i}`}>
                  <span className="w-1.5 h-1.5 rounded-full mt-1 shrink-0" style={{ backgroundColor: LEVEL_STYLE[r.severity]?.color }} />
                  <span className="text-[var(--oim-text-secondary)]">{r.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
